import { getTrndsClientSongTiktokAudioPostStats, TrndsClientSongTiktokAudioPostStats } from '@round/api';
import { useCallback, useMemo, useState } from 'react';
import debounce from 'lodash/debounce';
import useAbortableEffect from '../../../../../../Hooks/useAbortableEffect';

export default function useTopPosts({
    clientId,
    search,
    songId,
    page,
    page_size,
    ordering,
}: Parameters<typeof getTrndsClientSongTiktokAudioPostStats>[0]) {
    const [stats, setStats] = useState<TrndsClientSongTiktokAudioPostStats[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [hasErrorLoading, setHasErrorLoading] = useState(false);
    const [count, setCount] = useState(0);

    const fetchData = useCallback(
        async (requestInit?: RequestInit) => {
            try {
                setIsLoading(true);
                setHasErrorLoading(false);
                const response = await getTrndsClientSongTiktokAudioPostStats(
                    {
                        clientId,
                        songId,
                        search,
                        ordering,
                        page_size,
                        page,
                    },
                    requestInit
                );

                if (response.status === 200) {
                    setStats(response.data.results);
                    setCount(response.data.count);
                    return;
                }

                setHasErrorLoading(true);
            } catch (e) {
                if (e instanceof Error && e.name === 'AbortError') {
                    return;
                }

                setHasErrorLoading(true);
            } finally {
                setIsLoading(false);
            }
        },
        [clientId, ordering, page, page_size, search, songId]
    );

    const fetchDebounced = useMemo(() => debounce(fetchData, 700), [fetchData]);
    useAbortableEffect(
        (signal) => {
            fetchDebounced({ signal });
        },
        [fetchDebounced]
    );

    return {
        count,
        stats,
        hasErrorLoading,
        isLoading,
        reset: () => {
            setStats([]);
            setIsLoading(false);
            setHasErrorLoading(false);
            setCount(0);
        },
    };
}
